import type { LocalDto } from '@gl/shared';
import { Copy, Download, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { markQrDownloaded } from '@/features/resumen/first-steps-storage';
import { PrintQrButton } from './PrintQr';
import { displayUrl, downloadFile, qrDataUrl, qrSvg } from './qr';
import { copyToClipboard } from './useCopy';

interface QrCardProps {
  local: LocalDto;
  defaultUrl: string;
  /** URL del dominio propio ACTIVE, si hay. */
  customUrl: string | null;
}

export function QrCard({ local, defaultUrl, customUrl }: QrCardProps) {
  const qrUrl = customUrl ?? defaultUrl;
  const [preview, setPreview] = useState<{ url: string; src: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileBase = `qr-menu-${local.slug}`;

  useEffect(() => {
    let cancelled = false;
    qrDataUrl(qrUrl, 512)
      .then((src) => !cancelled && setPreview({ url: qrUrl, src }))
      .catch(() => !cancelled && toast.error('No pudimos generar el QR'));
    return () => {
      cancelled = true;
    };
  }, [qrUrl]);

  const src = preview?.url === qrUrl ? preview.src : null;

  const downloadPng = async () => {
    setBusy(true);
    try {
      downloadFile(await qrDataUrl(qrUrl, 1024), `${fileBase}.png`);
      markQrDownloaded(local.id);
    } catch {
      toast.error('No pudimos generar el PNG');
    } finally {
      setBusy(false);
    }
  };

  const downloadSvg = async () => {
    setBusy(true);
    try {
      const svg = await qrSvg(qrUrl);
      const href = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
      downloadFile(href, `${fileBase}.svg`);
      markQrDownloaded(local.id);
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
    } catch {
      toast.error('No pudimos generar el SVG');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tu menú</CardTitle>
        <CardDescription>Compartí el link o imprimí el QR para las mesas y el mostrador.</CardDescription>
      </CardHeader>
      <CardContent className="@container">
        <div className="grid gap-6 @xl:grid-cols-[minmax(0,1fr)_auto] @xl:items-start">
        <div className="grid min-w-0 gap-4">
          <div className="grid gap-1.5">
            <p className="text-sm font-medium">Dirección por defecto</p>
            <div className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
              <a
                href={defaultUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate font-mono text-sm hover:underline"
              >
                {displayUrl(defaultUrl)}
              </a>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label="Copiar dirección"
                onClick={() => void copyToClipboard(defaultUrl, 'Dirección copiada')}
              >
                <Copy />
              </Button>
              <Button variant="ghost" size="icon" className="size-8" aria-label="Abrir menú" asChild>
                <a href={defaultUrl} target="_blank" rel="noreferrer">
                  <ExternalLink />
                </a>
              </Button>
            </div>
          </div>

          <div className="rounded-md border px-3 py-2 text-sm">
            <span className="text-muted-foreground">El QR apunta a </span>
            <span className="font-mono break-all">{displayUrl(qrUrl)}</span>
            <span className="text-muted-foreground">
              {customUrl ? ' (tu dominio propio).' : ' (dirección por defecto). Si conectás un dominio propio y queda activo, el QR pasa a usarlo.'}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={busy} onClick={() => void downloadPng()}>
              <Download /> Descargar PNG
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => void downloadSvg()}>
              <Download /> Descargar SVG
            </Button>
            <PrintQrButton
              url={qrUrl}
              localName={local.name}
              logoUrl={local.logoUrl}
              primaryColor={local.primaryColor}
              disabled={busy}
              onPrint={() => markQrDownloaded(local.id)}
            />
          </div>
        </div>

        <div className="mx-auto flex size-64 items-center justify-center rounded-xl border bg-white p-2 order-first @xl:order-last">
          {src ? (
            <img src={src} alt={`QR del menú: ${displayUrl(qrUrl)}`} className="size-full" width={512} height={512} />
          ) : (
            <Spinner className="size-6 text-muted-foreground" />
          )}
        </div>
        </div>
      </CardContent>
    </Card>
  );
}
